import { defineComponent } from 'vue'
import moment from 'moment'
import AgGridColumnMethodMixins from '@/Mixins/SigmaClientNew/AgGridColumnMethods'
import { isServerFiltered, statusFilterValues, PAYMENT_LIST_STATUS_LABELS } from '@/Mixins/SigmaClientNew/transactionGridFilters'

/*
* AG Grid Column Defs here
*/
export default defineComponent({
  mixins: [AgGridColumnMethodMixins],
  data () {
    const serverFiltered = isServerFiltered(this.$route.name)
    const uniqueValues = (values) => [...new Set(values.filter(v => v !== null && v !== undefined && v !== ''))]

    const textFilterParams = () => {
      const params = { suppressAndOrCondition: true }
      if (serverFiltered) params.textCustomComparator = () => true
      return params
    }

    const textFloatingFilter = {
      floatingFilter: true,
      floatingFilterComponent: 'agGridTextFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true
      }
    }

    const serverFilterValue = serverFiltered ? { filterValueGetter: () => '' } : {}

    /**
     * The same, for a set filter whose criteria the query carries. A set filter keeps the rows whose
     * value is one of the ticked ones, so the row is handed every value the filter offers and counts
     * as whichever are ticked. Without this a column the rows read differently from what is ticked -
     * the payment types, ticked as CHECK and read as 'Check' - hides every row the API just returned.
     * @param getValues   The values the filter offers, the same ones it is built from
     */
    const serverSetFilterValue = (getValues) => serverFiltered ? { filterValueGetter: () => getValues() } : {}

    // Server side only the criteria the API understands are offered: an exact date, or a range
    const dateFilterParams = (comparator) => {
      if (serverFiltered) {
        return {
          suppressAndOrCondition: true,
          filterOptions: ['equals'],
          inRangeInclusive: false,
          comparator: () => 0
        }
      }
      const params = { suppressAndOrCondition: true }
      if (comparator) params.comparator = comparator
      return params
    }

    /**
     * Set filter values are read when the filter is opened so they follow the data currently loaded.
     * The server query is built from the same values, so what the grid keeps and what the API
     * returns stay in agreement.
     */
    const setFilterParams = (getValues, { valueFormatter } = {}) => {
      const params = {
        suppressAndOrCondition: true,
        values: (params) => params.success(uniqueValues(getValues())),
        refreshValuesOnOpen: true,
        newRowsAction: 'keep',
        suppressSorting: true,
        buttons: ['apply', 'cancel'],
        closeOnApply: true
      }
      // Filters whose stored value is not what the user should read, e.g. payment types
      if (valueFormatter) params.valueFormatter = valueFormatter
      return params
    }

    const statusLabel = (params) => PAYMENT_LIST_STATUS_LABELS[params.value] || params.value
    const statusValues = () => statusFilterValues(this.$route.name, this.baseQuery)

    // Names/codes of everything of a kind held in the store, e.g. every vendor on the loaded pages
    const storeValues = (getter, attribute) => () => uniqueValues((this.$store.getters[getter] || []).map(v => v && v[attribute]))
      .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }))

    const paymentTypeLabels = {
      CHECK: 'Check',
      WIRE: 'Wire Transfer',
      EFT: 'EFT',
      CHECK_MANUAL: 'Manual Check',
      NA: 'N/A'
    }
    const notifiedLabels = {
      'True': 'Sent',
      'False': 'Unsent'
    }
    const signatureApprovalLabels = {
      REQUESTED: 'REQUESTED',
      SIGNED: 'SIGNED'
    }

    return {
      rowSelect: {
        field: 'selection',
        maxWidth: 40,
        minWidth: 40,
        headerCheckboxSelection: true,
        checkboxSelection: true
      },
      ledgerColumn: {
        headerName: 'ID',
        field: 'ledger',
        colId: 'ledger',
        maxWidth: 60,
        minWidth: 60,
        sortable: true,
        sort: 'desc',
        sortingOrder: ['desc', 'asc'],
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles(),
        valueSetter: () => null
      },
      numberColumn: {
        headerName: 'Number',
        field: 'number',
        colId: 'number',
        maxWidth: 250,
        minWidth: 150,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles(),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueSetter: () => null
      },
      vendorColumn: {
        headerName: 'Vendor',
        field: 'vendor',
        colId: 'vendor',
        maxWidth: 300,
        minWidth: 200,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        // Vendors are searched through the API, not picked from the ones already loaded
        filter: 'agGridSearchFilter',
        filterParams: {
          searchAction: 'fetchVendorsWithQuery',
          entityKey: 'vendors',
          placeholder: 'Search vendors',
          emptyText: 'Search for a vendor to filter by',
          singleSelect: ['Checks', 'CheckProcessing', 'PaymentProcessing'].includes(this.$route.name),
          // The query is built from the ids picked here, so the rows are not narrowed again
          serverFiltered
        },
        floatingFilter: true,
        floatingFilterComponent: 'agGridSearchFloatingFilter',
        floatingFilterComponentParams: {
          suppressFilterButton: true,
          placeholder: 'Vendor'
        },
        cellStyle: this.getStyles('vendor'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueGetter: (params) => {
          if (params.data) {
            const vendor = params.data.vendor_data || params.data.vendor
            return vendor ? vendor.name : ''
          } else {
            return ''
          }
        },
        valueSetter: () => null
      },
      descriptionColumn: {
        headerName: 'Description',
        field: 'description',
        colId: 'description',
        maxWidth: 400,
        minWidth: 300,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles('description'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueGetter: (params) => params.data ? params.data.description : '',
        valueSetter: () => null
      },
      paymentTypeColumn: {
        headerName: 'Payment Type',
        field: 'payment_type',
        colId: 'payment_type',
        maxWidth: 200,
        minWidth: 100,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        // The stored payment types are filtered on, the labels are only what the user reads
        filterParams: setFilterParams(
          () => Object.keys(paymentTypeLabels),
          { valueFormatter: (params) => paymentTypeLabels[params.value] || params.value }
        ),
        ...serverSetFilterValue(() => Object.keys(paymentTypeLabels)),
        cellStyle: this.getStyles('payment_type'),
        valueGetter: (params) => {
          if (!params.data) return ''
          return paymentTypeLabels[params.data.payment_type] || params.data.payment_type || 'N/A'
        },
        valueSetter: () => null
      },
      amountColumn: {
        headerName: 'Amount',
        field: 'amount_decimal',
        colId: 'amount_decimal',
        maxWidth: 180,
        minWidth: 80,
        sortable: true,
        type: 'rightAligned',
        sortingOrder: ['desc', 'asc'],
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles('amount'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueFormatter: (params) => this.intlNumberFormatter(params.value || 0),
        valueGetter: (params) => {
          return (params.data && params.data.amount_decimal) ? Number(params.data.amount_decimal).toFixed(2) : '0.00'
        },
        valueSetter: () => null
      },
      dueDateColumn: {
        headerName: 'Due Date',
        field: 'due_date',
        colId: 'due_date',
        maxWidth: 100,
        minWidth: 100,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true
        },
        filterParams: dateFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles('date'),
        comparator: (date1, date2) => {
          const dateObj1 = new Date(date1)
          const dateObj2 = new Date(date2)
          return dateObj1.getTime() - dateObj2.getTime()
        },
        valueFormatter: (params) => {
          if (params.data && params.data.date_due) {
            const dueDate = params.data.date_due
            const dueDateList = dueDate.split('-')
            return dueDateList[1] + '-' + dueDateList[2].slice(0, 2) + '-' + dueDateList[0] // MM/DD/YYYY
          }
          return null
        },
        valueGetter: (params) => params.data ? params.data.date_due : '',
        valueSetter: () => null
      },
      dateColumn: {
        headerName: 'Date',
        field: 'date',
        colId: 'date',
        maxWidth: 100,
        minWidth: 100,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true
        },
        filterParams: dateFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles('date'),
        comparator: (date1, date2) => {
          const dateObj1 = new Date(date1)
          const dateObj2 = new Date(date2)
          return dateObj1.getTime() - dateObj2.getTime()
        },
        valueFormatter: (params) => {
          if (params.data && params.data.date) {
            const dateCol = params.data.date
            const dateList = dateCol.split('-')
            return dateList[1] + '-' + dateList[2].slice(0, 2) + '-' + dateList[0] // MM/DD/YYYY
          }
          return null
        },
        valueGetter: (params) => params.data ? params.data.date : '',
        valueSetter: () => null
      },
      dateRangeColumn: {
        headerName: 'Date',
        field: 'date',
        colId: 'date',
        maxWidth: 100,
        minWidth: 100,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        /**
         * The list is read over a span of dates, open at either end: ag-grid's own date filter asks
         * for a span as one criterion and will not apply it until both ends are given, so the dates
         * are picked here instead - from a date, up to one, or between the two.
         */
        filter: 'agGridDateRangeFilter',
        filterParams: {
          // The query is built from the dates picked, so the rows are not narrowed again
          serverFiltered,
          cellDateFormat: 'MM-DD-YYYY'
        },
        floatingFilter: true,
        floatingFilterComponent: 'agGridDateRangeFloatingFilter',
        floatingFilterComponentParams: {
          suppressFilterButton: true,
          placeholder: 'Date'
        },
        cellStyle: this.getStyles('date'),
        comparator: (date1, date2) => {
          const dateObj1 = new Date(date1)
          const dateObj2 = new Date(date2)
          return dateObj1.getTime() - dateObj2.getTime()
        },
        valueFormatter: (params) => {
          if (params.data && params.data.date) {
            const rangeDate = params.data.date
            const rangeDateList = rangeDate.split('-')
            return rangeDateList[1] + '-' + rangeDateList[2].slice(0, 2) + '-' + rangeDateList[0] // MM/DD/YYYY
          }
          return null
        },
        valueGetter: (params) => params.data ? params.data.date : '',
        valueSetter: () => null
      },
      batchColumn: {
        headerName: 'Batch',
        field: 'batch',
        colId: 'batch',
        maxWidth: 300,
        minWidth: 200,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        // Batches are searched through the API, not picked from the ones already loaded
        filter: 'agGridSearchFilter',
        filterParams: {
          searchAction: 'fetchBatchesWithQuery',
          entityKey: 'batches',
          placeholder: 'Search batches',
          emptyText: 'Search for a batch to filter by',
          // The list is read one batch at a time, so picking one is asking for it instead of the last
          singleSelect: true,
          // The query is built from the ids picked here, so the rows are not narrowed again
          serverFiltered
        },
        floatingFilter: true,
        floatingFilterComponent: 'agGridSearchFloatingFilter',
        floatingFilterComponentParams: {
          suppressFilterButton: true,
          placeholder: 'Batch'
        },
        cellStyle: this.getStyles('batch'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueGetter: (params) => (params.data && params.data.batch_data) ? params.data.batch_data.name : '',
        valueSetter: () => null
      },
      bankPaymentColumn: {
        headerName: 'Bank',
        field: 'bank',
        colId: 'bank',
        maxWidth: 300,
        minWidth: 200,
        editable: false,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        filterParams: setFilterParams(storeValues('getAllBanks', 'name')),
        // The query asks for the banks by id, so the names ticked here are not compared to the rows
        ...serverSetFilterValue(storeValues('getAllBanks', 'name')),
        cellStyle: this.getStyles('bank'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueGetter: (params) => {
          if (params.data) {
            const bank = params.data.bank_data || params.data.bank
            return bank ? bank.name : ''
          } else {
            return ''
          }
        },
        valueSetter: () => null
      },
      bankColumn: {
        headerName: 'Bank',
        field: 'bank',
        colId: 'bank',
        maxWidth: 300,
        minWidth: 200,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        // The list is read one bank at a time, so the banks are offered to be picked between rather
        // than ticked off. The query asks for them by id, so what is picked is not compared to the rows.
        filter: 'agGridSingleSelectFilter',
        filterParams: {
          values: storeValues('getAllBanks', 'name'),
          placeholder: 'Search banks',
          emptyText: 'No banks to filter by',
          noMatchText: 'No banks match that',
          serverFiltered
        },
        floatingFilter: true,
        floatingFilterComponent: 'agGridSearchFloatingFilter',
        floatingFilterComponentParams: {
          suppressFilterButton: true,
          placeholder: 'Bank'
        },
        cellStyle: this.getStyles('bank'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueGetter: (params) => {
          if (params.data) {
            let bank = null
            if (params.data.bank_data) {
              bank = params.data.bank_data
            } else if (params.data.batch_data && params.data.batch_data.bank) {
              bank = this.$store.getters.getBankById(params.data.batch_data.bank)
            } else {
              bank = params.data.bank
            }
            return bank ? bank.name : ''
          } else {
            return ''
          }
        },
        valueSetter: () => null
      },
      signatureApprovalsColumn: {
        headerName: 'Signature Approvals',
        field: 'signature_status',
        colId: 'signature_status',
        maxWidth: 250,
        minWidth: 150,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        // The stored payment types are filtered on, the labels are only what the user reads
        filterParams: setFilterParams(
          () => Object.keys(signatureApprovalLabels),
          { valueFormatter: (params) => signatureApprovalLabels[params.value] || params.value }
        ),
        cellStyle: this.getStyles('signature_status'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueGetter: (params) => params.data ? params.data.signature_status : '',
        valueSetter: () => null
      },
      isNotifiedColumn: {
        headerName: 'Email Status',
        field: 'is_notified',
        colId: 'is_notified',
        maxWidth: 160,
        minWidth: 80,
        editable: false,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        filterParams: setFilterParams(
          () => Object.keys(notifiedLabels),
          { valueFormatter: (params) => notifiedLabels[params.value] || params.value }
        ),
        ...serverSetFilterValue(() => Object.keys(notifiedLabels)),
        cellStyle: this.getStyles('is_notified'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueGetter: (params) => params.data ? ((params.data.payment_type === 'EFT') ? (params.data.is_notified ? 'Sent' : 'Unsent') : 'N/A') : '',
        valueSetter: () => null
      },
      actionsColumn: {
        field: 'Status',
        colId: 'status',
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        filterParams: setFilterParams(statusValues, { valueFormatter: ['Payments', 'Checks'].includes(this.$route.name) ? statusLabel : undefined }),
        // The query asks for the statuses ticked, and a row can hold one the view does not offer
        ...serverSetFilterValue(statusValues),
        cellRenderer: 'agGridBtnRender',
        cellStyle: this.getStyles('actions'),
        valueGetter: (params) => params.data ? params.data.status : '',
        minWidth: 220,
        maxWidth: 270
      }
    }
  }
})
